import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarCheck, TrendingUp, Users, RotateCcw } from 'lucide-react';
import { useParticipationStats } from '../../hooks/useParticipationStats';

const ParticipationStatsTab: React.FC = () => {
  useUiLanguage();
  const { t } = useTranslation(['admin', 'common']);
  const { participationStats, studentStats, isLoading, closePastSessions, refetchStats } = useParticipationStats();
  const [isClosingSessions, setIsClosingSessions] = useState(false);

  const handleClosePastSessions = async () => {
    setIsClosingSessions(true);
    await closePastSessions();
    setIsClosingSessions(false);
  };

  const formatDateToDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const getAttendanceRateBadge = (rate: number) => {
    if (rate >= 80) return <Badge className="bg-green-100 text-green-800">{t('common:status.excellent')} ({rate}%)</Badge>;
    if (rate >= 60) return <Badge className="bg-yellow-100 text-yellow-800">{t('common:status.good')} ({rate}%)</Badge>;
    if (rate >= 40) return <Badge className="bg-orange-100 text-orange-800">{t('common:status.fair')} ({rate}%)</Badge>;
    return <Badge className="bg-red-100 text-red-800">{t('common:status.needsImprovement')} ({rate}%)</Badge>;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-900 mx-auto mb-4"></div>
            <p>{t('common:messages.loading')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {uiText("Participation Statistics")}</CardTitle>
          <CardDescription>
            {uiText("View attendance trends and participation analytics")}</CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
            <Button
              onClick={handleClosePastSessions}
              disabled={isClosingSessions}
              className="bg-red-900 hover:bg-red-800 text-xs sm:text-sm p-2 sm:p-3 w-full sm:w-auto"
              size="sm"
            >
              <CalendarCheck className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{isClosingSessions ? t('admin:participationStats.processing') : uiText("Close Past Sessions")}</span>
              <span className="sm:hidden">{isClosingSessions ? t('admin:participationStats.processing') : uiText("Close Sessions")}</span>
            </Button>
            <Button
              variant="outline"
              onClick={refetchStats}
              className="border-red-200 text-red-700 hover:bg-red-50 text-xs sm:text-sm p-2 sm:p-3 w-full sm:w-auto"
              size="sm"
            >
              <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{uiText("Refresh Data")}</span>
              <span className="sm:hidden">{uiText("Refresh")}</span>
            </Button>
          </div>

          <Tabs defaultValue="overview" className="space-y-4 sm:space-y-6">
            <TabsList className="grid w-full grid-cols-2 h-auto sm:h-10">
              <TabsTrigger value="overview" className="text-xs sm:text-sm p-2 sm:p-3">
                <span className="hidden sm:inline">{uiText("Student Overview")}</span>
                <span className="sm:hidden">{uiText("Overview")}</span>
              </TabsTrigger>
              <TabsTrigger value="detailed" className="text-xs sm:text-sm p-2 sm:p-3">
                <span className="hidden sm:inline">{uiText("Detailed History")}</span>
                <span className="sm:hidden">{uiText("Details")}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    {uiText("Attendance Summary")}</CardTitle>
                </CardHeader>
                <CardContent>
                  {studentStats.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {t('admin:participationStats.noParticipationData')}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin:participationStats.name')}</TableHead>
                          <TableHead>{t('admin:participationStats.totalSessions')}</TableHead>
                          <TableHead>{t('admin:participationStats.attended')}</TableHead>
                          <TableHead>{t('admin:participationStats.attendanceRate')}</TableHead>
                          <TableHead>{t('admin:participationStats.lastAttendance')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {studentStats.map((stats) => (
                          <TableRow key={stats.student_id}>
                            <TableCell className="font-medium">{stats.student_name}</TableCell>
                            <TableCell>{stats.total_sessions}</TableCell>
                            <TableCell>{stats.attended_sessions}</TableCell>
                            <TableCell>{getAttendanceRateBadge(stats.attendance_rate)}</TableCell>
                            <TableCell>
                              {stats.last_attendance ? formatDateToDDMMYYYY(stats.last_attendance) : t('admin:participationStats.never')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="detailed">
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin:participationStats.participationHistory')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {participationStats.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {t('admin:participationStats.noParticipationHistory')}
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin:participationStats.date')}</TableHead>
                          <TableHead>{t('admin:participationStats.session')}</TableHead>
                          <TableHead>{t('admin:participationStats.student')}</TableHead>
                          <TableHead>{t('admin:participationStats.ageGroup')}</TableHead>
                          <TableHead>{t('admin:participationStats.attendance')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {participationStats.map((stat) => (
                          <TableRow key={stat.id}>
                            <TableCell>{formatDateToDDMMYYYY(stat.session_date)}</TableCell>
                            <TableCell>{stat.session_title}</TableCell>
                            <TableCell>
                              {stat.student.first_name} {stat.student.last_name}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                 {stat.age_group === 'kids' ? t('admin:participationStats.kids') :
                                  stat.age_group === 'teens' ? t('admin:participationStats.teens') :
                                  stat.age_group === 'adults' ? t('admin:participationStats.adults') : t('admin:participationStats.allAges')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={stat.attended ? 'default' : 'secondary'}>
                                {stat.attended ? t('admin:participationStats.attended') : t('admin:participationStats.missed')}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParticipationStatsTab;